import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { SectionHeading } from '../ui/SectionHeading';
import { PageLightbox } from './PageLightbox';
import { previewPages } from '../../data/previewPages';
import { padNumber } from '../../utils/format';
export function InsidePreview() {
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const total = previewPages.length;
  const page = previewPages[index];
  const go = (step: number) => setIndex(current => (current + step + total) % total);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(-1);
    }
  };
  const arrowClass = 'inline-flex h-14 w-14 items-center justify-center border-2 border-gold text-gold transition-colors duration-150 hover:bg-gold hover:text-ink';
  return <section id="look-inside" aria-labelledby="inside-title" className="bg-ink py-20 text-white lg:py-24" data-id="element-70" data-mp="hkpnv6y86h64">
      <div className="mx-auto max-w-page px-5 lg:px-8" data-id="element-71" data-mp="dkfl035qc6vh">
        <SectionHeading id="inside-title" eyebrow="Real pages from the guide" lead="Look inside" highlight="the material" intro="Five real pages from the 40-page guide. Tap a page to enlarge it and read the details." data-id="element-72" data-mp="ra98oi5i0l3m" />

        <div role="region" aria-roledescription="carousel" aria-label="Guide page previews" onKeyDown={handleKeyDown} className="mt-12 grid gap-10 lg:grid-cols-2 lg:gap-14" data-id="element-73" data-mp="w92l0791u627">
          <div data-id="element-74" data-mp="15atttkyvyt0a">
            <button type="button" onClick={() => setLightboxOpen(true)} aria-label={`Enlarge page ${page.page}: ${page.title}`} className="group relative mx-auto block w-full bg-white p-2 sm:max-w-[460px]" data-id="element-75" data-mp="15aa9xypodujm">
              <img key={page.src} src={page.src} alt={`Page ${page.page} of the guide: ${page.title}`} className="mx-auto block h-auto max-h-[78vh] w-auto max-w-full" data-id="element-76" data-mp="1az0rgqxft4o4" />
              <span className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 bg-ink/85 px-3 py-2 text-sm font-bold text-white" data-id="element-77" data-mp="1ky9et0xsrl1i">
                <ZoomInIcon aria-hidden="true" className="h-4 w-4 text-gold" data-id="element-78" data-mp="1txmsy6wv8xsk" />
                Tap to enlarge
              </span>
            </button>

            <div className="mt-6 flex items-center justify-center gap-6" data-id="element-79" data-mp="1ytv3f704c2te">
              <button type="button" onClick={() => go(-1)} aria-label="Previous page" className={arrowClass} data-id="element-80" data-mp="23nzdw08lesz8">
                <ChevronLeftIcon aria-hidden="true" className="h-7 w-7" data-id="element-81" data-mp="28ky447p4yfs5" />
              </button>
              <p className="min-w-[88px] text-center font-display text-[28px] font-bold tabular-nums" data-id="element-82" data-mp="2coxee8jtoid2">
                <span className="text-gold" data-id="element-83" data-mp="2hmp9fbfrkklh">{padNumber(index + 1)}</span>
                <span className="text-white/60" data-id="element-84" data-mp="6055z8lki040"> / {padNumber(total)}</span>
              </p>
              <button type="button" onClick={() => go(1)} aria-label="Next page" className={arrowClass} data-id="element-85" data-mp="yy0xhat7f11">
                <ChevronRightIcon aria-hidden="true" className="h-7 w-7" data-id="element-86" data-mp="3t4tk5wkf40uv" />
              </button>
            </div>
          </div>

          <div data-id="element-87" data-mp="3o6jntimyiu7o">
            <div aria-live="polite" data-id="element-88" data-mp="3o71qoy44k6ok">
              <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-gold" data-id="element-89" data-mp="optou3c59q30">Page {page.page}</p>
              <h3 className="mt-2 font-display text-[32px] font-bold uppercase leading-[1.02] sm:text-[40px]" data-id="element-90" data-mp="kp1et7vg7h5o">
                {page.title}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-white/80 sm:text-lg" data-id="element-91" data-mp="ay6q7wuqc5h2">{page.description}</p>
            </div>

            <ul className="mt-8 space-y-3" aria-label="Choose a page" data-id="element-92" data-mp="18dsz7d2sqv7w">
              {previewPages.map((item, itemIndex) => {
              const active = itemIndex === index;
              return <li key={item.page} data-id="element-93" data-mp="n6a86oqhq2xh" data-mpi={itemIndex}>
                    <button type="button" onClick={() => setIndex(itemIndex)} aria-current={active ? 'true' : undefined} className={`flex min-h-[64px] w-full items-center gap-4 border p-2.5 pr-4 text-left transition-colors duration-150 ${active ? 'border-gold bg-gold/10' : 'border-white/15 bg-card hover:border-white/40'}`} data-id="element-94" data-mp="n9fww2v7j2so">
                      <img src={item.src} alt="" loading="lazy" className="block h-14 w-auto shrink-0 bg-white" data-id="element-95" data-mp="1awecyzff08q2" />
                      <span className="min-w-0" data-id="element-96" data-mp="x3exgxfmueii">
                        <span className={`block text-[12px] font-bold uppercase tracking-[0.14em] ${active ? 'text-gold' : 'text-white/60'}`} data-id="element-97" data-mp="123svdmnis82t">
                          Page {item.page}
                        </span>
                        <span className="block text-base font-bold leading-snug text-white" data-id="element-98" data-mp="1uloqq7vlojlj">{item.title}</span>
                      </span>
                    </button>
                  </li>;
            })}
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-white/60" data-id="element-99" data-mp="1zwaog294htsd">
              Previews show the current edition. Use ← and → to move between pages.
            </p>
          </div>
        </div>
      </div>

      {lightboxOpen && <PageLightbox index={index} onChange={setIndex} onClose={() => setLightboxOpen(false)} data-id="element-100" data-mp="1l0l1o1w45cml" />}
    </section>;
}